/* eslint-disable react-hooks/exhaustive-deps */
import {useNavigate, useParams} from "react-router";
import {
  ControlledInput,
  ControlledSelect,
  DownloadProgressModal,
  FlexibleDateTimePicker,
  PrimaryButton,
  SecondaryButton,
} from "../../components";
import {withProtectedRoute} from "../../utils/HOC/withProtectedRoute";
import {Controller, useForm} from "react-hook-form";
import {useEffect, useState} from "react";
import {useDropLocations} from "../../store/useDropLocations";
import {useBeevisionMachines} from "../../store/useBeevisionMachines";
import {ReactComponent as LeftArrow} from "../../assets/icons/arrows/left-arrow.svg";
import {useAuth} from "../../store/useAuth";
import axios from "axios";
import {LOG} from "../../utils/logUtil";

type Inputs = {
  name: string;
  serialNumber: string;
  location: string;
  apiUrl?: string;
};

function EditBeevisionMachineScreen() {
  /** Params */
  const params = useParams();
  /** Hooks */
  const { user } = useAuth();
  const isAdmin = Number(user?.user_role) === 1 || Number(user?.user_role) === 5;
  const { dropLocations, getDropLocations } = useDropLocations();
  const {
    updateBeevisionMachine,
    updatedBeevisionMachine,
    clearUpdatedBeevisionMachine,
    getBeevisionMachine,
    beevisionMachine,
  } = useBeevisionMachines();
  const navigate = useNavigate();
  const {
    reset,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>();
  /** State */
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

  // Modal form for date range
  const {
    control: modalControl,
    handleSubmit: handleModalSubmit,
    watch: modalWatch,
    reset: modalReset,
  } = useForm({
    defaultValues: {
      From: null,
      To: null,
      DownloadAll: false,
    },
  });

  /** Effects */
  useEffect(() => {
    if (updatedBeevisionMachine) {
      clearUpdatedBeevisionMachine();
      navigate(-1);
    }
  }, [updatedBeevisionMachine]);

  useEffect(() => {
    getBeevisionMachine(params?.id as string);
    getDropLocations();
  }, []);

  useEffect(() => {
    if (beevisionMachine) {
      reset({
        name: beevisionMachine?.name,
        serialNumber: beevisionMachine?.serialNumber,
        location: beevisionMachine?.location,
        apiUrl: beevisionMachine?.apiUrl,
      });
    }
  }, [beevisionMachine]);

  /** Callbacks */
  const goBack = () => navigate(-1);

  const onSubmit = async (data: Inputs) => {
    updateBeevisionMachine(params?.id as string, data);
  };

  const openDownloadModal = () => {
    setIsDownloadModalOpen(true);
    modalReset({ From: null, To: null, DownloadAll: false });
  };

  const handleDownloadChoice = (data: any) => {
    setIsDownloadModalOpen(false);
    setTimeout(() => {
      downloadAllImages(data);
    }, 200);
  };

  const downloadAllImages = async (modalData?: any) => {
    setIsDownloading(true);
    setDownloadProgress(0);
    try {
      let body;
      if (modalData?.DownloadAll) {
        body = { From: "", To: "", DownloadAll: 1 };
      } else {
        body = {
          From: modalData?.From?.toISOString().slice(0, 16),
          To: modalData?.To?.toISOString().slice(0, 16),
          DownloadAll: 0,
        };
      }
      const proxyBody = {
        target: `${beevisionMachine?.apiUrl}/downloadImagesZip`,
        originalBody: body
      };
      const response = await axios.post(
        `/proxy/downloadImagesZip`,
        proxyBody,
        {
          responseType: "blob",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/zip",
          },
        }
      );
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "downloaded_images.zip";
      link.onclick = () => {
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(link);
        }, 150);
      };
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      LOG.error("Error downloading images", error);
    } finally {
      setTimeout(() => {
        setIsDownloading(false);
        setDownloadProgress(0);
      }, 1000);
    }
  };

  return (
    <div>
      {/* Download Progress Modal */}
      <DownloadProgressModal
        isOpen={isDownloading}
        progress={downloadProgress}
        onClose={() => setIsDownloading(false)}
      />
      {/* Download Choice Modal */}
      {isDownloadModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold text-dark mb-2">Download Images</h3>
            <p className="text-sm text-gray-600 mb-4">
              Would you like to download all images, or select a date range?
            </p>
            <form
              onSubmit={handleModalSubmit(handleDownloadChoice)}
              className="flex flex-col gap-2"
            >
              <div className="flex items-center mb-2">
                <Controller
                  name="DownloadAll"
                  control={modalControl}
                  render={({ field }) => (
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={e => field.onChange(e.target.checked)}
                      id="downloadAllCheckbox"
                      className="mr-2"
                    />
                  )}
                />
                <label htmlFor="downloadAllCheckbox" className="text-base text-dark">
                  Download all images
                </label>
              </div>
              {!modalWatch("DownloadAll") && (
                <>
                  <FlexibleDateTimePicker
                    control={modalControl}
                    name="From"
                    label="From"
                    rules={{ required: true }}
                  />
                  <FlexibleDateTimePicker
                    control={modalControl}
                    name="To"
                    label="To"
                    rules={{ required: true }}
                    minDate={modalWatch("From") || new Date("2024-01-01")}
                    minTime={modalWatch("From") || new Date("2024-01-01")}
                  />
                </>
              )}
              <div className="flex flex-row justify-end gap-2 mt-4">
                <SecondaryButton type="button" onClick={() => setIsDownloadModalOpen(false)} label="Cancel" />
                <PrimaryButton type="submit" label="Download" />
              </div>
            </form>
          </div>
        </div>
      )}
      <div className="flex flex-row items-center justify-between mb-6">
        <div className="flex flex-row items-center">
          <LeftArrow onClick={goBack} className="mr-3 cursor-pointer" />
          <h1 className="font-satoshi text-2xl font-bold text-dark">
            Edit Beevision Machine
          </h1>
        </div>
        {isAdmin ? (
          <div>
            <PrimaryButton
              label="Download all images"
              onClick={openDownloadModal}
            />
          </div>
        ) : null}
      </div>
      <div className="flex flex-row">
        <div className="w-full sm:w-1/2 bg-white border border-gray-border drop-shadow-md">
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              Update Beevision Machine details
            </p>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="w-full px-7 py-7">
            <ControlledInput
              control={control}
              error={Boolean(errors?.name)}
              label="Name"
              placeholder="Beevision Machine name"
              name="name"
              rules={{ required: true }}
            />
            <ControlledInput
              control={control}
              error={Boolean(errors?.serialNumber)}
              label="Serial number"
              placeholder="Beevision machine serial number"
              name="serialNumber"
              rules={{ required: true }}
            />
            <ControlledSelect
              name="location"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.location)}
              label="Beevision Machine Location"
              // @ts-ignore
              options={dropLocations?.map((location) => ({
                // @ts-ignore
                title: location?.cost_place as string,
                // @ts-ignore
                _id: location._id as string,
              }))}
              placeholder="Pick location here"
            />
            <ControlledInput
              control={control}
              error={Boolean(errors?.apiUrl)}
              label="API Url"
              placeholder="Beevision Machine API url"
              name="apiUrl"
              rules={{ required: false }}
            />
            <div className="flex flex-row items-center justify-end">
              <SecondaryButton type="button" onClick={goBack} label="Cancel" />
              <PrimaryButton type="submit" className="ml-4" label="Save" />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default withProtectedRoute(EditBeevisionMachineScreen);
